'use client';

import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useContext, useState } from 'react';

import { FormattedMessage } from '@flipster/i18n';
import { Modal } from '@flipster/react/design-system/modal';
import { css } from '@flipster/styled-system/css';
import { stack } from '@flipster/styled-system/patterns';
import { Input } from '@flipster/ui';
import { getErrorCode } from '@flipster/utils/error';
import segment from '@flipster/utils/segment';
import { CompetitionsEvent } from '@flipster/utils/segment/events/competitions';

import { SegmentPage } from '../../../misc/segment/segment-page';
import { registerCompetitionBunja } from '../action';
import { CompetitionIdContext } from '../state';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onExit: () => void;
}

export const InviteCodeModal = ({ isOpen, onClose, onExit }: Props) => {
  const id = useContext(CompetitionIdContext);
  const [invitationCode, setInvitationCode] = useState('');
  const { registerCompetition, registerCompetitionErrorMessageAtom, resetRegisterCompetitionError } =
    useBunja(registerCompetitionBunja);
  const registrationErrorMessage = useAtomValue(registerCompetitionErrorMessageAtom);

  return (
    <>
      <SegmentPage name={CompetitionsEvent.CompetitionEnterCodeModalView} context={{ competition_id: id }} />
      <Modal.Root isOpen={isOpen} onClose={onClose} onExit={onExit} mode='fullModal' size='small'>
        <Modal.Header>
          <Modal.Title label='Register with an Invite Code' />
        </Modal.Header>
        <Modal.Content>
          <div className={stack({ gap: 12, mt: 16 })}>
            <div className={stack({ gap: 4 })}>
              <Input
                placeholder='Enter code shared by the host'
                value={invitationCode}
                onFocus={() => {
                  segment.track(CompetitionsEvent.CompetitionEnterCodeModalEnterCodeClick, { competition_id: id });
                }}
                onChange={(e) => {
                  setInvitationCode(e.currentTarget.value);
                  resetRegisterCompetitionError();
                }}
                isError={Boolean(registrationErrorMessage)}
                isFull
              />
              {registrationErrorMessage && (
                <p className={css({ textStyle: 'Text/S/Regular', color: 'content.state.negative' })}>
                  {registrationErrorMessage}
                </p>
              )}
            </div>
          </div>
        </Modal.Content>
        <Modal.Footer>
          <Modal.Button
            label={<FormattedMessage id='Autogen_common.r251126.s_Register' />}
            hierarchy='primary'
            size='large'
            disabled={!invitationCode || Boolean(registrationErrorMessage)}
            isLoading={false}
            onClick={async () => {
              try {
                segment.track(CompetitionsEvent.CompetitionEnterCodeModalJoinClick, { competition_id: id });
                await registerCompetition(invitationCode);
                onClose();
              } catch (error) {
                if (getErrorCode(error) !== 'api.tradingCompetition.userInvitationCodeNotMatched') {
                  onClose();
                }
              }
            }}
          />
        </Modal.Footer>
      </Modal.Root>
    </>
  );
};
